import {
  planPercentageLabel,
  type PlanAllocationRangeInput
} from '../../display-values/lumpsumPlanPercentage'
import { getCategoryBucketItemCount } from '../categoryBucketsUtils'
import {
  PlanCategoryBucketKey,
  PLAN_ACTION_FILTER_BUCKET,
  PlansActionFilter
} from '../../lumpsum/api/types'

export type CategoryWithPlanBuckets = {
  category?: string | null
  sellRecommendations?: unknown[] | null
  buyRecommendations?: unknown[] | null
  underReviewRecommendations?: unknown[] | null
  executedRecommendations?: unknown[] | null
  invalidatedRecommendations?: unknown[] | null
  noActionFunds?: unknown[] | null
}

export const PLAN_CATEGORY_BUCKETS: {
  bucketKey: PlanCategoryBucketKey
  getFundCount: (category: CategoryWithPlanBuckets | null | undefined) => number
}[] = [
  {
    bucketKey: PlanCategoryBucketKey.sellRecommendations,
    getFundCount: (category) => getCategoryBucketItemCount(category?.sellRecommendations)
  },
  {
    bucketKey: PlanCategoryBucketKey.buyRecommendations,
    getFundCount: (category) => getCategoryBucketItemCount(category?.buyRecommendations)
  },
  {
    bucketKey: PlanCategoryBucketKey.underReviewRecommendations,
    getFundCount: (category) => getCategoryBucketItemCount(category?.underReviewRecommendations)
  },
  {
    bucketKey: PlanCategoryBucketKey.executedRecommendations,
    getFundCount: (category) => getCategoryBucketItemCount(category?.executedRecommendations)
  },
  {
    bucketKey: PlanCategoryBucketKey.invalidatedRecommendations,
    getFundCount: (category) => getCategoryBucketItemCount(category?.invalidatedRecommendations)
  },
  {
    bucketKey: PlanCategoryBucketKey.noActionFunds,
    getFundCount: (category) => getCategoryBucketItemCount(category?.noActionFunds)
  }
]

export function resolvePlanCategoryKey(
  category: CategoryWithPlanBuckets | null | undefined,
  categoryIndex: number
): string {
  const categoryKey = category?.category?.trim() ?? ''
  if (categoryKey.length > 0) {
    return categoryKey
  }
  return `category-${categoryIndex}`
}

export function buildPlanCategoryBucketCompositeKey({
  categoryKey,
  bucketKey
}: {
  categoryKey: string
  bucketKey: PlanCategoryBucketKey
}): string {
  return `${categoryKey}:${bucketKey}`
}

function getPlanCategoryBucketsForCategory(category: CategoryWithPlanBuckets | null | undefined) {
  const hasExecutedRecommendations = getCategoryBucketItemCount(category?.executedRecommendations) > 0
  if (!hasExecutedRecommendations) {
    return PLAN_CATEGORY_BUCKETS
  }

  const executedBucket = PLAN_CATEGORY_BUCKETS.find(
    (bucket) => bucket.bucketKey === PlanCategoryBucketKey.executedRecommendations
  )
  const otherBuckets = PLAN_CATEGORY_BUCKETS.filter(
    (bucket) => bucket.bucketKey !== PlanCategoryBucketKey.executedRecommendations
  )

  if (!executedBucket) {
    return PLAN_CATEGORY_BUCKETS
  }

  return [executedBucket, ...otherBuckets]
}

export function hasVisiblePlanListContent(
  categories: CategoryWithPlanBuckets[] | null | undefined,
  actionFilter: PlansActionFilter
): boolean {
  if (!Array.isArray(categories) || categories.length <= 0) {
    return false
  }

  return categories.some((category) => getVisiblePlanCategoryBucketKeys(category, actionFilter).length > 0)
}

export function isPlansListFullyEmpty(categories: CategoryWithPlanBuckets[] | null | undefined): boolean {
  return !hasVisiblePlanListContent(categories, PlansActionFilter.ALL)
}

type IdealCategoryWithFunds = {
  idealFunds?: unknown[] | null
}

export function isIdealPlansListFullyEmpty(
  categories: IdealCategoryWithFunds[] | null | undefined
): boolean {
  if (!Array.isArray(categories) || categories.length <= 0) {
    return true
  }
  return !categories.some((category) => getCategoryBucketItemCount(category?.idealFunds) > 0)
}

export function getVisiblePlanCategoryBucketKeys(
  category: CategoryWithPlanBuckets | null | undefined,
  actionFilter: PlansActionFilter
): PlanCategoryBucketKey[] {
  if (!category) {
    return []
  }

  return getPlanCategoryBucketsForCategory(category)
    .filter(({ bucketKey, getFundCount }) => {
      if (getFundCount(category) <= 0) {
        return false
      }
      if (actionFilter === PlansActionFilter.ALL) {
        return true
      }
      return bucketKey === PLAN_ACTION_FILTER_BUCKET[actionFilter]
    })
    .map(({ bucketKey }) => bucketKey)
}

export function computeIdealFundAllocatedAmount(
  currentValue: number | null | undefined,
  fundIdealAllocation: PlanAllocationRangeInput | null | undefined
): number {
  const portfolioValue = currentValue ?? 0
  const absolutePercent = fundIdealAllocation?.absolute ?? 0
  if (portfolioValue > 0 && absolutePercent > 0) {
    return (portfolioValue * absolutePercent) / 100
  }
  return 0
}

/** Fund-card secondary: lower–upper% ideal. Same rounded label shows one percent. No absolute fallback. */
export function formatFundIdealRangeTrailingLabel(
  idealAllocation?: PlanAllocationRangeInput | null
): string {
  const lowerLimit = idealAllocation?.lowerLimit ?? idealAllocation?.lowerimit
  const upperLimit = idealAllocation?.upperLimit
  if (typeof lowerLimit !== 'number' || typeof upperLimit !== 'number') {
    return ''
  }
  const lowerLabel = planPercentageLabel({ rawPercentage: lowerLimit })
  const upperLabel = planPercentageLabel({ rawPercentage: upperLimit })
  if (lowerLabel === upperLabel) {
    return `${upperLabel} ideal`
  }
  return `${lowerLabel.slice(0, -1)}–${upperLabel} ideal`
}
